// Copyright (c) Bentley Systems
import { TreeNodeData } from "./DataModels/tree-node-data";
import { ActionVm } from "../Action/action-vm";
import { EtObjectType } from "./DataModels/et-object-type";

// ---------------------------------------------------------------------------
// Primitive helpers
// ---------------------------------------------------------------------------

/**
 * Safely converts an unknown API value to a string.
 * Returns `undefined` for objects, arrays, and null/undefined so callers can
 * distinguish "no value" from an empty string.
 */
export const toSafeString = (value: unknown): string | undefined => {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }
  return undefined;
};

// Shared types
// ---------------------------------------------------------------------------

export type TreeNodeRawData = Record<string, unknown>;
export type TreeNodeDataWithRaw = TreeNodeData & { rawData?: TreeNodeRawData };
export type TreeNodeVisibilityData = TreeNodeRawData | TreeNodeDataWithRaw;

// ---------------------------------------------------------------------------
// Raw-data accessor helpers
// ---------------------------------------------------------------------------

export const getRecordValue = (record: TreeNodeRawData, fieldName?: string | null): unknown => {
  if (!fieldName) return undefined;
  return record[fieldName];
};

export const hasRawData = (
  nodeData: TreeNodeVisibilityData
): nodeData is TreeNodeDataWithRaw & { rawData: TreeNodeRawData } => {
  return (
    typeof nodeData === "object" &&
    nodeData !== null &&
    "rawData" in nodeData &&
    typeof (nodeData as TreeNodeDataWithRaw).rawData === "object" &&
    (nodeData as TreeNodeDataWithRaw).rawData !== null
  );
};

export const getNodeVisibilityValue = (
  nodeData: TreeNodeVisibilityData,
  fieldName: string
): unknown => {
  if (hasRawData(nodeData) && fieldName in nodeData.rawData) {
    return nodeData.rawData[fieldName];
  }
  if (fieldName in nodeData) {
    return nodeData[fieldName as keyof TreeNodeDataWithRaw];
  }
  return undefined;
};

// ---------------------------------------------------------------------------
// Icon helpers
// ---------------------------------------------------------------------------

export const parseIconName = (iconName: string | undefined): string | undefined => {
  if (!iconName) return undefined;
  const prefix = "itwinicon:";
  const lower = iconName.toLowerCase();
  return lower.indexOf(prefix) > -1 ? lower.substring(prefix.length).trim() : iconName;
};

/**
 * Derives a data URI from a raw base64 icon string stored in the database.
 *
 * The backend `Icon` table stores icons as file bytes converted to base64 but does NOT
 * include a MIME type in the API response. We detect the format from the leading bytes:
 *   - `/9j/`  → JPEG  (SOI marker)
 *   - `iVBOR` → PNG   (PNG signature)
 *   - `R0lGO` → GIF   (GIF87a / GIF89a)
 * Falls back to `image/png` for any unrecognised prefix.
 *
 * TODO: proper fix — add `MimeType` to `IconDto` in DawApi/Models/Dtos/IconDto.cs.
 */
export const getBase64IconSrc = (base64Str: string): string => {
  if (base64Str.startsWith("/9j/")) return `data:image/jpeg;base64,${base64Str}`;
  if (base64Str.startsWith("iVBOR")) return `data:image/png;base64,${base64Str}`;
  if (base64Str.startsWith("R0lGO")) return `data:image/gif;base64,${base64Str}`;
  // TODO: backend should expose MimeType in IconDto — see DawApi/Models/Dtos/IconDto.cs
  return `data:image/png;base64,${base64Str}`;
};

// ---------------------------------------------------------------------------
// Raw node mapping — shared between buildHierarchicalTree & transformTreeData
// ---------------------------------------------------------------------------

/** Minimal action shape needed for node-field visibility resolution. */
type EtActionRef = { actionId: number; actionType: string };

/** Configuration context required by mapRawNodeFields. */
export type EtNodeFieldContext = {
  etObjectTypes?: EtObjectType[];
  etActions?: EtActionRef[];
};

/** Field-name descriptors from EtParentChildTreeType. */
export type RawNodeFieldNames = {
  childNameField: string;
  childSublabelField: string;
  childObjectTypeField: string;
};

/** The resolved display/action fields common to every tree node. */
export type RawNodeMappedFields = {
  label: string;
  sublabel: string;
  hasChildren: boolean;
  objectTypes: EtObjectType[] | undefined;
  actionIds: number[];
  icon: string | undefined;
  iconBase64Str: string | undefined;
};

/**
 * Maps a single raw API record to the display/action fields shared by both the
 * hierarchical (root) and flat (child) tree-node transforms.
 *
 * Extracted to eliminate duplication between `buildHierarchicalTree` and
 * `transformTreeData` in `useEtreeData`, and to allow unit testing of the
 * mapping logic independently of the hook.
 */
export const mapRawNodeFields = (
  item: TreeNodeRawData,
  index: number,
  fieldNames: RawNodeFieldNames,
  config: EtNodeFieldContext,
  actionsMap: Map<number, ActionVm>
): RawNodeMappedFields => {
  const label = toSafeString(getRecordValue(item, fieldNames.childNameField)) ?? `Item ${index}`;
  const sublabel = toSafeString(getRecordValue(item, fieldNames.childSublabelField)) ?? "";
  const typeCode = getRecordValue(item, fieldNames.childObjectTypeField);
  const hasChildren = item._hasChildren === true || item._hasChildren === "true";

  const objectTypes = config.etObjectTypes?.filter((ot) => ot.typeCode === typeCode);
  const actionIds: number[] = [];
  objectTypes?.forEach((objectType) => {
    const etAction = config.etActions?.find(
      (a) => a.actionId === objectType.actionId && a.actionType === "O"
    );
    if (etAction?.actionId) {
      const action = actionsMap.get(etAction.actionId);
      if (action && isActionVisibleForNode(action, item)) {
        actionIds.push(etAction.actionId);
      }
    }
  });

  return {
    label,
    sublabel,
    hasChildren,
    objectTypes,
    actionIds,
    icon: parseIconName(objectTypes?.[0]?.icon?.name),
    iconBase64Str: objectTypes?.[0]?.icon?.base64Str,
  };
};

// ---------------------------------------------------------------------------
// Visibility evaluation
// ---------------------------------------------------------------------------

export const isActionVisibleForNode = (
  action: ActionVm,
  nodeData: TreeNodeVisibilityData
): boolean => {
  if (!action.visCompareValue || !action.visCompareType) return true;

  const fieldValue = getNodeVisibilityValue(nodeData, action.visCompareValue);
  if (fieldValue === undefined || fieldValue === null) return false;

  const { visFromValue: fromValue, visToValue: toValue } = action;
  if (fromValue === undefined || toValue === undefined) return true;

  const compareValues = (a: unknown, b: unknown): number => {
    const numA = parseFloat(String(a));
    const numB = parseFloat(String(b));
    if (!isNaN(numA) && !isNaN(numB)) {
      return numA < numB ? -1 : numA > numB ? 1 : 0;
    }
    return String(a).toLowerCase().localeCompare(String(b).toLowerCase());
  };

  if (fromValue === toValue) return compareValues(fieldValue, fromValue) === 0;
  return compareValues(fieldValue, fromValue) >= 0 && compareValues(fieldValue, toValue) <= 0;
};

// ---------------------------------------------------------------------------
// Tree search helper
// ---------------------------------------------------------------------------

export const findNodeById = (nodes: TreeNodeData[], id: string): TreeNodeData | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.subItems.length > 0) {
      const found = findNodeById(node.subItems, id);
      if (found) return found;
    }
  }
  return null;
};

// ---------------------------------------------------------------------------
// Tree expansion helpers
// ---------------------------------------------------------------------------

/**
 * Calculates which nodes should be expanded based on expandLevel.
 * expandLevel defines the depth: 0 = root only, 1 = root + first level, etc.
 */
export const calculateInitialExpandedNodes = (
  nodes: TreeNodeData[],
  expandLevel: number
): Record<string, boolean> => {
  const expandedNodes: Record<string, boolean> = {};

  const traverse = (nodeList: TreeNodeData[], currentDepth: number) => {
    for (const node of nodeList) {
      if (currentDepth < expandLevel) {
        expandedNodes[node.id] = true;
        if (node.subItems.length > 0) {
          traverse(node.subItems, currentDepth + 1);
        }
      }
    }
  };

  traverse(nodes, 0);
  return expandedNodes;
};

// ---------------------------------------------------------------------------
// Expansion-intent model helpers
// ---------------------------------------------------------------------------

/**
 * Computes which nodes should be expanded by policy (expandLevel).
 */
export const computePolicyCandidates = (
  nodes: TreeNodeData[],
  expandLevel: number
): Record<string, boolean> => {
  const policyCandidates: Record<string, boolean> = {};

  const traverse = (nodeList: TreeNodeData[], currentDepth: number) => {
    for (const node of nodeList) {
      if (currentDepth < expandLevel) {
        policyCandidates[node.id] = true;
        if (node.subItems.length > 0) {
          traverse(node.subItems, currentDepth + 1);
        }
      }
    }
  };

  traverse(nodes, 0);
  return policyCandidates;
};

/**
 * Computes effective expansion state by merging policy, manual intent, and current expandability.
 * Formula: (policyCandidates U manualExpanded) - manualCollapsed, filtered to expandable nodes
 */
export const computeEffectiveExpanded = (
  policyCandidates: Record<string, boolean>,
  manualExpanded: Record<string, boolean>,
  manualCollapsed: Record<string, boolean>,
  nodes: TreeNodeData[]
): Record<string, boolean> => {
  const effectiveExpanded: Record<string, boolean> = {};

  const allNodeIds = new Set<string>();
  const collectNodeIds = (nodeList: TreeNodeData[]) => {
    for (const node of nodeList) {
      allNodeIds.add(node.id);
      if (node.subItems.length > 0) {
        collectNodeIds(node.subItems);
      }
    }
  };
  collectNodeIds(nodes);

  for (const nodeId of Array.from(allNodeIds)) {
    const isInPolicy = policyCandidates[nodeId] === true;
    const isManualExpanded = manualExpanded[nodeId] === true;
    const isManualCollapsed = manualCollapsed[nodeId] === true;

    if (!isManualCollapsed && (isInPolicy || isManualExpanded)) {
      effectiveExpanded[nodeId] = true;
    }
  }

  return effectiveExpanded;
};
